import {
  API_URL,
  ApiError,
  USER_KEY,
  api,
  clearSessionHint,
  hasSessionHint,
  setSessionHint,
} from "../api/client";
import { OAuthProvider } from "../lib/oauth";
import { BetaStatus, Invite } from "../types/Beta";
import { PublicUser } from "../types/Social";
import { Visibility } from "../types/Tracking";
import User from "../types/User";
import { WorkType } from "../types/Work";
import { forgetApiResponses } from "./queryClient";
import { useApi } from "./useApi";

// The session and the account: signing in, up and out, the session's
// user, and the account's own settings. None of it waits offline.

type AuthResponse = { user: User };

// Whether this device has a session, as far as it can tell without asking.
export const signedIn = hasSessionHint;

// The signed-in user, cached next to the session hint so a returning
// visit (or a cold start of the installed app) shows them at once.
export const cachedUser = (): User | null => {
  if (!hasSessionHint()) return null;
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) ?? "null");
  } catch {
    return null;
  }
};

export const rememberUser = (user: User) =>
  localStorage.setItem(USER_KEY, JSON.stringify(user));

// A new session: what was loaded signed out isn't what it should see.
export const beginSession = () => {
  forgetApiResponses();
  setSessionHint();
};

// The session's end: its user and every response kept in memory go.
export const endSession = () => {
  clearSessionHint();
  localStorage.removeItem(USER_KEY);
  forgetApiResponses();
};

/** Whether `err` says the session is gone (a 401); a network error isn't. */
export const signedOut = (err: unknown): boolean =>
  err instanceof ApiError && err.status === 401;

export const me = () => api.get<User>("/auth/me");

// These send credentials even though no one is signed in yet: a browser
// only stores a cross-origin response's cookies when the request did.
export const login = (email: string, password: string) =>
  api.post<AuthResponse>("/auth/login", { email, password });

export const signup = (account: {
  email: string;
  username: string;
  password: string;
  ref?: string;
  invite?: string;
}) => api.post<AuthResponse>("/auth/signup", account);

export const logout = () => api.post("/auth/logout", undefined);

export const linkLogin = (token: string) =>
  api.post<AuthResponse>("/auth/link-login", { token });

/** Where the browser goes to sign in with a provider: the API redirects on. */
export const oauthStartUrl = (provider: OAuthProvider): string =>
  `${API_URL}/auth/oauth/${provider}/start`;

export const oauthSignup = (account: {
  username: string;
  ref?: string;
  invite?: string;
}) => api.post<AuthResponse>("/auth/oauth/signup", account);

export const startPasswordReset = (email: string) =>
  api.post("/auth/password-reset/start", { email }, false);

export const resetPassword = (token: string, password: string) =>
  api.post<AuthResponse>("/auth/password-reset", { token, password });

export const verifyEmail = (token: string) =>
  api.post("/auth/verify-email", { token });

export const resendVerification = () =>
  api.post("/auth/verify-email/start", {});

/** What to say when asking for another verification link fails. */
export const resendError = (err: unknown): string =>
  err instanceof ApiError && err.status === 429
    ? "That's a few links this hour already. Try again later."
    : "Couldn't send it. Please try again.";

// The beta: whether it's full, and whether an invite code is good.
export const betaPath = (code: string | undefined): string =>
  code ? `/beta?invite=${encodeURIComponent(code)}` : "/beta";

export const betaStatus = (code: string) =>
  api.get<BetaStatus>(betaPath(code), false);

export const joinWaitlist = (entry: {
  email: string;
  apps: string[];
  media: WorkType[];
  invite?: string;
}) => api.post("/beta/waitlist", entry, false);

// The account's settings. Each answers with the user as it now is.
export const updateMe = (changes: Partial<User> & Record<string, unknown>) =>
  api.patch<User>("/auth/me", changes);

export const setAvatar = (file: File | null) => {
  if (!file) return api.del<User>("/auth/me/avatar");
  const form = new FormData();
  form.append("file", file);
  return api.put<User>("/auth/me/avatar", form);
};

export const revokeSessions = () =>
  api.post("/auth/revoke-sessions", undefined);

export const deleteAccount = (password: string) =>
  api.del("/auth/me", true, { password });

// Every entry on the shelf shown, or hidden, at once.
export const shareAll = (visibility: Visibility) =>
  api.post<{ updated: number }>("/library/visibility", { visibility });

// Only the entries with a review shown; `dryRun` counts them instead.
export const shareReviews = (dryRun = false) =>
  api.post<{ updated: number }>("/library/visibility", {
    visibility: "public",
    reviewed: true,
    ...(dryRun && { dryRun }),
  });

// The beta's state for a code (or none), signed out.
export const useBeta = (code: string | undefined) =>
  useApi<BetaStatus>(betaPath(code), { auth: false });

// Who you've blocked; your invites to give.
export const useBlocks = () => useApi<PublicUser[]>("/auth/me/blocks");
export const useInvites = () => useApi<{ invites: Invite[] }>("/beta/invites");
